import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Select, {
    ActionMeta,
    components,
    MenuProps,
    OptionProps,
    StylesConfig,
    ValueContainerProps
} from 'react-select';
import isEmpty from 'lodash/isEmpty';

import {
    THEME_BLACK,
    THEME_PRIMARY,
    THEME_SILVER,
    THEME_SMOKY,
    THEME_WHITE,
    themeColor
} from '../../../styles/basics/colors';
import { ProductGroupIcon } from './ProductGroupIcon';
import { MATERIAL_ICON_EXPAND_LESS, MATERIAL_ICON_EXPAND_MORE } from '../Button/Icon';
import { ProductGroupButton } from './ProductGroupButton';
import { FONT_SMALL, themeFontSize } from '../fontSizes';
import { BUTTON_SIZE_SMALL } from '../Button/themes';

export function validateCssPositionAndSize(value: string | number | undefined) {
    const re = new RegExp('^[+-]?[0-9]+.?([0-9]+)?(px|em|ex|%|in|cm|mm|pt|pc)$|(auto)');
    return !!String(value)?.match(re);
}

const StSelectedLine = styled.span<{ bgColor: string }>`
    font-size: ${themeFontSize(FONT_SMALL)};
    background-color: ${props => props.bgColor};
    color: ${props => props.bgColor};
    display: flex;
    align-items: center;
`;

const StMenu = styled(components.Menu)`
    border: 1px solid ${themeColor(THEME_SILVER)};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 3px;
`;

const StButton = styled(ProductGroupButton).attrs({ className: 'bulkActionDropdownTrigger' })`
    display: flex;
    min-width: 100%;
`;

const StRightAlignedIcon = styled(ProductGroupIcon).attrs({ className: 'bulkActionDropdownIndicatorIcon ' })`
    margin-left: auto;
`;

const StIconMarginLeft = styled(ProductGroupIcon)`
    margin-left: 10px;
`;

const StIconMarginRight = styled(ProductGroupIcon).attrs({ className: 'bulkActionDropdownActionIcon' })`
    margin-right: 5px;
`;

const customStyles = (width: number | string, menuListHeight: number | undefined, optionStyles = {}): StylesConfig => {
    return {
        option: (styles, { isFocused }) => {
            return {
                ...styles,
                backgroundColor: isFocused ? themeColor(THEME_SMOKY) : themeColor(THEME_WHITE),
                color: isFocused ? themeColor(THEME_PRIMARY) : themeColor(THEME_BLACK),
                ...optionStyles
            };
        },
        control: provided => ({
            ...provided,
            border: 'none',
            borderColor: 'unset',
            borderRadius: 'unset',
            boxShadow: 'unset',
            minHeight: 'unset'
        }),
        dropdownIndicator: provided => ({
            ...provided,
            color: 'black',
            padding: 0
        }),
        menu: provided => {
            return {
                ...provided,
                width: 320,
                borderRadius: 'unset',
                top: '60%',
                zIndex: 420
            };
        },
        container: provided => ({
            ...provided,
            width
        }),
        menuList: provided => ({
            ...provided,
            height: menuListHeight
        })
    };
};

interface Props {
    displayValue?: string;
    actionIcon: string;
    forceCloseMenu?: boolean;
    onChange: (newValue: unknown, actionMeta: ActionMeta<unknown>) => void;
    options: {
        label: JSX.Element;
        value: number | undefined;
        group?: {
            readonly productGroupId?: number | undefined;
            readonly scope?: string | undefined;
            readonly productGroupName?: string | undefined;
        };
    }[];
    value?: { newValue: unknown; actionMeta: ActionMeta<unknown> };
    width?: number | string;
    menuListHeight?: number;
    className?: string;
    optionStyles?: StylesConfig;
    isClearable?: boolean;
    customComponents?: any;
    isDisabled?: boolean;
    isLoading?: boolean;
    dataDisplayNameValueContainer?: string;
}

export const ProductGroupDropdown = ({
    displayValue,
    actionIcon,
    forceCloseMenu,
    options,
    value,
    width = '100%',
    menuListHeight,
    className,
    optionStyles,
    isClearable,
    customComponents,
    isDisabled,
    isLoading,
    onChange,
    dataDisplayNameValueContainer
}: Props) => {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    let wrapperRef = useRef<HTMLDivElement>(null);

    const handleClickOutside = useCallback((event: any) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
            closeMenu();
        }
    }, []);

    useEffect(() => {
        if (menuIsOpen && forceCloseMenu) {
            setMenuIsOpen(false);
        }
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [forceCloseMenu, handleClickOutside, menuIsOpen]);

    const ValueContainer = (props: ValueContainerProps & { width: number }) => {
        const indicatorIcon = props.selectProps.menuIsOpen ? MATERIAL_ICON_EXPAND_LESS : MATERIAL_ICON_EXPAND_MORE;

        return (
            <StButton
                dataDisplayname={dataDisplayNameValueContainer}
                onClick={toggleMenuIsOpen}
                size={BUTTON_SIZE_SMALL}
                theme="default"
            >
                <StIconMarginRight
                    dataDisplayname={'BulkActionDropdownActionIcon'}
                    color={THEME_PRIMARY}
                    icon={actionIcon}
                />
                {displayValue}
                <StRightAlignedIcon
                    dataDisplayname={'BulkActionDropdownIndicatorIcon'}
                    color={THEME_PRIMARY}
                    icon={indicatorIcon}
                />
            </StButton>
        );
    };

    function toggleMenuIsOpen() {
        setMenuIsOpen(!menuIsOpen);
    }

    function closeMenu() {
        setMenuIsOpen(false);
    }

    const Option = (props: OptionProps) => {
        const option = <components.Option {...props}>{props.children}</components.Option>;
        const bgColor = props?.isFocused ? themeColor(THEME_SMOKY) : themeColor(THEME_WHITE);
        const color = props?.isFocused ? themeColor(THEME_PRIMARY) : themeColor(THEME_BLACK);

        return (
            <StSelectedLine color={color} bgColor={bgColor} onClick={closeMenu}>
                <StIconMarginLeft color={THEME_PRIMARY} icon={actionIcon} /> {option}
            </StSelectedLine>
        );
    };

    const Menu = (props: MenuProps) => {
        return <StMenu {...props}>{props.children}</StMenu>;
    };

    if (isEmpty(options)) {
        console.error('No Options provided');
        return null;
    }

    const DropdownIndicator = () => null;
    const IndicatorSeparator = () => null;

    const widthWithFallback = validateCssPositionAndSize(width) ? width : '100%';
    const styles = customStyles(widthWithFallback, menuListHeight, optionStyles);

    return (
        <div className={className} ref={wrapperRef} data-displayname="bulkActionDropdownWrapper">
            <Select
                styles={styles}
                components={{
                    ...customComponents,
                    ValueContainer,
                    DropdownIndicator,
                    Option,
                    Menu,
                    IndicatorSeparator
                }}
                isDisabled={isDisabled}
                isLoading={isLoading}
                isClearable={isClearable}
                options={options}
                onChange={onChange}
                value={value}
                menuIsOpen={menuIsOpen}
            />
        </div>
    );
};
